import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';

import style from './dropdown.less';
import { DROPDOWN_WRAPPER_CONTAINER } from '../../../../constants/element-ids';
import usePositioning from '../../../hooks/dropdown/use-positioning';
import GlobalEventListener from '../../global-event-listener';
import GlobalEventListenerRoot from '../../global-event-listener/root';
// @ts-expect-error less import

/**
 * @typedef {import('./dropdown-types.d.ts').DropdownOverlayComponent} DropdownOverlayComponent
 * @typedef {import('./dropdown-types.d.ts').ComputedStyle} ComputedStyle
 */

/**
 * @type {DropdownOverlayComponent}
 */
const DropdownOverlay = React.forwardRef(
  (
    {
      children,
      closeWhenChildrenClicked,
      containerAllowedMeToClose,
      dropdownMenuShouldHaveWidthOfButton,
      isOpen,
      menuPosition,
      onBlur,
      onClose,
      onFocus,
      onKeyDown,
      isNested = false,
      targetRef,
    },
    ref,
  ) => {
    const child = /** @type {JSX.Element} */ (React.Children.only(children));
    const {
      position,
      ref: dropdownRef,
      style: dropdownStyle,
      windowBoundingBox: { height: windowBoundingBoxHeight },
    } = usePositioning(targetRef, menuPosition);

    const dropdownMenuStyle = useMemo(() => {
      const boundingBox = targetRef.current && targetRef.current.getBoundingClientRect();
      /**
       * @type {ComputedStyle}
       */
      const computedStyle = {
        ...dropdownStyle,
        width: !dropdownMenuShouldHaveWidthOfButton || !boundingBox ? 'auto' : boundingBox.width,
      };
      if (isNested) {
        computedStyle.maxHeight = windowBoundingBoxHeight - dropdownStyle.top;
        computedStyle.left = dropdownStyle.left + boundingBox.width * 2;
      }
      return computedStyle;
    }, [dropdownMenuShouldHaveWidthOfButton, dropdownStyle, isNested, targetRef, windowBoundingBoxHeight]);

    const handleDocumentClick = useCallback(() => {
      if (isOpen) {
        onClose();
      }
    }, [isOpen, onClose]);

    const handleDropdownClick = useCallback(
      (e) => {
        if (containerAllowedMeToClose && containerAllowedMeToClose(e)) {
          onClose();
        } else if (dropdownRef.current && dropdownRef.current.contains(e.target) && closeWhenChildrenClicked) {
          onClose();
        }
      },
      [closeWhenChildrenClicked, containerAllowedMeToClose, dropdownRef, onClose],
    );

    let dropdownMenuWrapperNode = global.document.getElementById(DROPDOWN_WRAPPER_CONTAINER);

    if (!dropdownMenuWrapperNode) {
      dropdownMenuWrapperNode = global.document.createElement('div');
      dropdownMenuWrapperNode.id = DROPDOWN_WRAPPER_CONTAINER;
      global.document.body.appendChild(dropdownMenuWrapperNode);
    }

    const dropdownContent = React.cloneElement(child, {
      className: classnames(child.props.className, style['drop-menu'], style[position]),
      ref: dropdownRef,
      style: dropdownMenuStyle,
      // if child is React component, data-position can be used as a prop to add custom styles/behavior according to dropdown position
      // passed as a custom data- attribute so that if child is a dom element no unknown attribute warning will be thrown
      'data-position': position,
    });

    if (isOpen) {
      return ReactDOM.createPortal(
        <GlobalEventListener onClick={handleDocumentClick}>
          {/* @ts-expect-error - TODO: remove this line when types are added to the GlobalEventListenerRoot */}
          <GlobalEventListenerRoot ref={ref} onBlur={onBlur} onFocus={onFocus}>
            <GlobalEventListener onClick={handleDropdownClick} onKeyDown={onKeyDown}>
              <div className='dropdown-menu-top-level-wrapper' style={dropdownMenuStyle}>
                {dropdownContent}
              </div>
            </GlobalEventListener>
          </GlobalEventListenerRoot>
        </GlobalEventListener>,
        dropdownMenuWrapperNode,
      );
    }

    return null;
  },
);

DropdownOverlay.displayName = 'DropdownOverlay';
export default DropdownOverlay;
